import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Link2, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export function LinkedAccounts() {
  const qc = useQueryClient();
  const { data, error } = useQuery({ queryKey: ["linked-accounts"], queryFn: () => api.get("/me/linked-accounts").then((r) => r.data) });
  const refresh = () => qc.invalidateQueries({ queryKey: ["linked-accounts"] });
  const run = async (fn, msg) => { try { await fn(); toast.success(msg); refresh(); } catch (err) { toast.error(formatApiError(err)); } };
  const active = (data?.identities || []).filter((i) => i.status === "active");
  return (
    <section className="panel space-y-4 p-6" data-testid="linked-accounts">
      <p className="eyebrow flex items-center gap-2"><Link2 className="h-4 w-4" />Linked accounts</p>
      <p className="text-xs text-slate-500">Optional. The Planar Foundry works fully without Discord. A linked account never grants staff or community powers.</p>
      {error && <p className="text-xs text-red-300" role="alert" data-testid="linked-accounts-error">{formatApiError(error)}</p>}
      {data?.providers.map((p) => {
        const mine = active.find((i) => i.provider === p.provider);
        return (
          <div key={p.provider} className="flex flex-wrap items-center gap-3 rounded-md border border-[#2A3447] p-3" data-testid={`linked-provider-${p.provider}`}>
            <span className="text-sm text-amber-100">{p.label}</span>
            {mine ? <>
              <span className="flex items-center gap-1 text-xs text-emerald-300" data-testid={`linked-identity-${mine.identity_id}`}><ShieldCheck className="h-3 w-3" />{mine.external_username || mine.external_id} · verified {mine.verified_at?.slice(0, 10)}</span>
              <button className="ml-auto text-xs text-red-300" onClick={() => run(() => api.delete(`/me/linked-accounts/${mine.identity_id}`), "Unlinked")} data-testid={`linked-unlink-${mine.identity_id}`}>Unlink</button>
            </> : <span className="ml-auto text-xs text-slate-500" data-testid={`linked-status-${p.provider}`}>{p.enabled ? "Not linked" : `Not available yet (planned for ${p.planned})`}</span>}
          </div>
        );
      })}
      {data?.managed_communities > 0 && (
        <div className="space-y-2" data-testid="guild-links">
          <p className="text-xs text-slate-400">Discord servers for communities you administer</p>
          {!data.guild_links.length && <p className="text-xs text-slate-500" data-testid="guild-links-empty">No server links. Server linking needs Discord verification (planned for Phase 8).</p>}
          {data.guild_links.map((g) => (
            <div key={g.link_id} className="flex flex-wrap items-center gap-2 rounded-md border border-[#2A3447] p-3 text-xs" data-testid={`guild-link-${g.link_id}`}>
              <span className="text-amber-100">{g.community?.name}</span><span className="font-mono text-slate-400">server {g.guild_id}</span>
              <span className={g.status === "active" ? "text-emerald-300" : "text-copper-300"} data-testid={`guild-link-status-${g.link_id}`}>{g.status.replace("_", " ")}</span>
              <button className="ml-auto text-red-300" onClick={() => run(() => api.delete(`/guild-links/${g.link_id}`), "Server link revoked")} data-testid={`guild-link-revoke-${g.link_id}`}>Revoke</button>
            </div>))}
        </div>)}
    </section>
  );
}
